/**
 * languesInterface — registre des langues DANS LESQUELLES L'APPLICATION S'AFFICHE.
 *
 * À ne pas confondre avec `consts/languageConstants.js`, qui liste les 59 langues
 * dans lesquelles un ASSISTANT peut répondre à un élève. Les deux questions sont
 * distinctes et le resteront : un enseignant pilote son assistant en français et
 * cet assistant répond en soninké à un élève allophone. Offrir l'INTERFACE en
 * soninké serait un tout autre engagement — il faudrait la traduire, la relire,
 * et la tenir à jour à chaque écran ajouté.
 *
 * Ce registre est la source unique. En dépendent :
 *   - l'initialisation d'i18next (`i18n/index.js`) ;
 *   - la résolution de la langue au démarrage (`i18n/detection.js`) ;
 *   - le sélecteur de langue (`composants/SelecteurLangue.js`) ;
 *   - la direction du document, `lang` et `dir` (`i18n/appliquerLangueAuDocument.js`) ;
 *   - le filtre de langue du mode Explorer, qui envoie ce code au serveur.
 *
 * Il a un jumeau côté serveur : `serveur/db/languesInterface.php`. Les deux
 * doivent déclarer les mêmes codes, les mêmes noms, les mêmes directions et les
 * mêmes disponibilités. `LanguesInterfaceSyncTest` compare les deux moitiés.
 *
 * ⚠️ Le filtre de l'Explorer porte sur la langue d'interface de CELUI QUI
 * PUBLIE (`users.langue_interface`), pas sur la langue que parle l'assistant.
 * Ce registre n'a donc plus à faire le pont vers `assistant.langue_chatbot` :
 * le code part tel quel au serveur. La raison du choix est écrite en tête de
 * `clauseLangueAuteur()`, côté PHP.
 */

export const LANGUES_INTERFACE = {
    fr: {
        code: 'fr',
        // Le nom s'écrit TOUJOURS dans la langue elle-même : quelqu'un qui
        // cherche sa langue dans une liste cherche « Deutsch », pas « Allemand ».
        nom: 'Français',
        direction: 'ltr',
        intl: 'fr-FR',
        disponible: true,
    },
    en: {
        code: 'en',
        nom: 'English',
        direction: 'ltr',
        intl: 'en-GB',
        disponible: true,
    },
    es: {
        code: 'es',
        nom: 'Español',
        direction: 'ltr',
        intl: 'es-ES',
        disponible: true,
    },
    de: {
        code: 'de',
        nom: 'Deutsch',
        direction: 'ltr',
        intl: 'de-DE',
        disponible: true,
    },
    ar: {
        code: 'ar',
        nom: 'العربية',
        direction: 'rtl',
        intl: 'ar',
        disponible: true,
    },
};

/** La langue de repli. Elle est toujours complète : c'est elle qu'on voit quand une clé manque. */
export const LANGUE_PAR_DEFAUT = 'fr';

/** Tous les codes déclarés, traduits ou non. */
export const CODES_DECLARES = Object.keys(LANGUES_INTERFACE);

/**
 * Les seules langues qu'on propose à l'usager.
 *
 * `disponible: false` laisse déclarer une langue avant de l'avoir traduite : le
 * filtre de l'Explorer et le registre la connaissent, le sélecteur ne la montre
 * pas encore. C'est ce qui permet de traduire une langue à la fois sans livrer
 * une interface à moitié française.
 */
export const languesDisponibles = () =>
    CODES_DECLARES.filter((code) => LANGUES_INTERFACE[code].disponible);

/** Un code est-il une langue d'interface que l'on sait afficher ? */
export const estLangueDisponible = (code) =>
    Boolean(code && LANGUES_INTERFACE[code]?.disponible);

/** La direction d'écriture d'une langue ('ltr' par défaut, y compris pour un code inconnu). */
export const directionDe = (code) => LANGUES_INTERFACE[code]?.direction || 'ltr';

/**
 * La locale complète à passer à `Intl` / `toLocaleDateString`.
 * Le code court suffirait le plus souvent, mais `fr` seul laisse le navigateur
 * choisir la région, et une date sud-américaine ne s'écrit pas comme une espagnole.
 */
export const localeIntlDe = (code) =>
    LANGUES_INTERFACE[code]?.intl || LANGUES_INTERFACE[LANGUE_PAR_DEFAUT].intl;

/** Le nom d'une langue, écrit dans cette langue. Rend le code brut s'il est inconnu. */
export const nomDe = (code) => LANGUES_INTERFACE[code]?.nom || code;
